import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, Clock, Building2, Eye, CheckCircle, RefreshCw,
} from "lucide-react";
import type { TenantIncident } from "@/hooks/useTenantIncidents";

const severityConfig: Record<string, { class: string; label: string }> = {
  critical: { class: "bg-destructive text-destructive-foreground", label: "Critical" },
  high: { class: "bg-destructive/80 text-destructive-foreground", label: "High" },
  medium: { class: "bg-amber-500 text-white", label: "Medium" },
  low: { class: "bg-muted text-muted-foreground", label: "Low" },
};

const categoryIcons: Record<string, string> = {
  onboarding: "🚀",
  billing: "💳",
  support: "🎫",
  compliance: "📋",
  ops: "⚙️",
};

interface IncidentCardProps {
  incident: TenantIncident;
  onInvestigate?: (incident: TenantIncident) => void;
  onAcknowledge?: (incident: TenantIncident) => void;
  onResolve?: (incident: TenantIncident) => void;
  compact?: boolean;
}

export function IncidentCard({
  incident,
  onInvestigate,
  onAcknowledge,
  onResolve,
  compact = false,
}: IncidentCardProps) {
  const sev = severityConfig[incident.severity] ?? severityConfig.low;
  const timeAgo = getTimeAgo(incident.last_seen_at);

  if (compact) {
    return (
      <div
        className="flex items-center gap-3 p-2 rounded-md hover:bg-accent/50 cursor-pointer transition-colors"
        onClick={() => onInvestigate?.(incident)}
      >
        <Badge className={`${sev.class} text-[10px] px-1.5 py-0 shrink-0`}>
          {sev.label}
        </Badge>
        <span className="text-xs truncate flex-1">{incident.title}</span>
        <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo}</span>
      </div>
    );
  }

  return (
    <Card className="transition-colors hover:border-primary/30">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="text-lg shrink-0 mt-0.5">
            {categoryIcons[incident.category] ?? "📌"}
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${sev.class} text-[10px] px-1.5 py-0`}>
                {sev.label}
              </Badge>
              <Badge variant="outline" className="text-[10px] capitalize">
                {incident.category}
              </Badge>
              {incident.occurrence_count > 1 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                  ×{incident.occurrence_count}
                </Badge>
              )}
              <Badge
                variant={incident.status === "open" ? "default" : "secondary"}
                className="text-[10px] capitalize ml-auto"
              >
                {incident.status}
              </Badge>
            </div>

            <h4 className="text-sm font-semibold">{incident.title}</h4>
            <p className="text-xs text-muted-foreground line-clamp-2">{incident.summary}</p>

            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {incident.tenant_name ?? "Unknown"}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {timeAgo}
              </span>
            </div>

            <div className="flex items-center gap-1.5 pt-1">
              {onInvestigate && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => onInvestigate(incident)}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Investigate
                </Button>
              )}
              {onAcknowledge && incident.status === "open" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => onAcknowledge(incident)}
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Acknowledge
                </Button>
              )}
              {onResolve && (incident.status === "open" || incident.status === "acknowledged") && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => onResolve(incident)}
                >
                  Resolve
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function getTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
